import { FormProvider, UseFormReturn, useForm } from 'react-hook-form'

import { Button } from '@factorialco/deprecated-design-system'
import {
  AdditionalCompensationFormFieldsFragment,
  PayrollConceptSelectFieldFragment,
} from 'generated/resources/graphql'
import { uniqueId } from 'lodash'

import ModalComponent from 'modules/core/components/Modal'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import {
  InMemoryAdditionalCompensation,
  useContractFlow,
} from 'modules/contracts/lib/contractFlowContext'

import { AdditionalCompensationFormFields } from '../AdditionalCompensationForm'

type CreateAdditionalCompensationFormProps = {
  useFormMethods: UseFormReturn<AdditionalCompensationFormFieldsFragment>
  payrollConcepts: PayrollConceptSelectFieldFragment[]
}
function CreateAdditionalCompensationForm({
  useFormMethods,
  payrollConcepts,
}: CreateAdditionalCompensationFormProps) {
  return (
    <FormProvider {...useFormMethods}>
      <AdditionalCompensationFormFields payrollConcepts={payrollConcepts} />
    </FormProvider>
  )
}

type CreateAdditionalCompensationFormModalProps = {
  payrollConcepts: PayrollConceptSelectFieldFragment[]
  onClose?: () => void
}

function CreateAdditionalCompensationFormModal({
  onClose,
  payrollConcepts,
}: CreateAdditionalCompensationFormModalProps) {
  const { additionalCompensations, setAdditionalCompensations, contractVersion } = useContractFlow()
  const methods = useForm<AdditionalCompensationFormFieldsFragment>({
    defaultValues: {
      contractVersionId: contractVersion?.id,
    },
  })

  const onSubmitForm = async () => {
    const isValid = await methods.trigger()

    if (!isValid) {
      return
    }

    const values = methods.getValues()
    const newAdditionalCompensation: InMemoryAdditionalCompensation = {
      tmpID: uniqueId(),
      ...values,
    }
    setAdditionalCompensations([...additionalCompensations, newAdditionalCompensation])

    track(trackingEvents.compensations.createCompensation, {
      type: 'contract',
    })

    onClose && onClose()
  }

  return (
    <ModalComponent
      illustration="edit-file-filled"
      title={i18n.t(
        'contract_versions.additional_compensation.create_additional_compensation_form_modal.title'
      )}
      description={i18n.t(
        'contract_versions.additional_compensation.create_additional_compensation_form_modal.subtitle'
      )}
      onClose={onClose}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t(
              'contract_versions.additional_compensation.create_additional_compensation_form_modal.close_text'
            )}
          </Button>
          <Button disabled={!methods.formState.isValid} onClick={onSubmitForm}>
            {i18n.t(
              'contract_versions.additional_compensation.create_additional_compensation_form_modal.save'
            )}
          </Button>
        </>
      }
    >
      <CreateAdditionalCompensationForm
        useFormMethods={methods}
        payrollConcepts={payrollConcepts}
      />
    </ModalComponent>
  )
}

export { CreateAdditionalCompensationFormModal }
